'use client';

import type { Attachment } from '@langbridge-builder/design-system';
import { CHAT_PASSWORD_HEADER, type LangbridgeConfig } from './api';
import { DEFAULT_CONFIG } from './settings';

/**
 * Attachment upload against the langbridge storage API. Sends files to public
 * storage and returns a GET URL so the agent receives a `url` multimodal part
 * instead of inline base64 (base64 would be re-persisted into session history /
 * checkpoints every turn). Two transports, chosen by runtime environment:
 *   - web → `POST {apiBase}/storage/presign` then a direct `PUT` to S3.
 *   - desktop → `POST {uploadBase}/storage/upload` (multipart) to the CENTRAL
 *     server, because the local sidecar has no S3 credentials. The Electron shell
 *     injects `uploadBase`/`uploadPassword` (see settings.ts / desktop main).
 * Contract mirrored from `../langbridge` storage.controller + storage.dto.
 */

/** Allowed MIME types (mirrors the backend storage allow-list). */
export const UPLOAD_ALLOWED_MIME: readonly string[] = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/svg+xml',
  'application/pdf',
  'text/plain',
  'text/markdown',
];

/** Max upload size (bytes). The desktop `/upload` path enforces this server-side. */
export const UPLOAD_MAX_BYTES = 25 * 1024 * 1024;

/** A user-facing upload failure (validation or network). */
export class UploadError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'UploadError';
  }
}

/** `POST /storage/presign` response (web). */
interface PresignResponse {
  uploadUrl: string;
  fileUrl: string;
  key: string;
  contentType: string;
  expiresIn: number;
  requiredHeaders: Record<string, string>;
}

/** `POST /storage/upload` response (desktop proxy). */
interface ProxyResponse {
  fileUrl: string;
  key: string;
  contentType: string;
}

/** Running inside the Electron desktop shell (preload exposes this bridge). */
function isDesktop(): boolean {
  if (typeof window === 'undefined') return false;
  return !!(window as unknown as { langbridgeBackend?: unknown }).langbridgeBackend;
}

interface UploadTarget {
  mode: 'presign' | 'proxy';
  base: string;
  password?: string;
}

/**
 * Resolve where uploads go. Desktop → the injected central server (proxy upload);
 * web → the same langbridge server as agent runs (presigned PUT). `uploadBase`/
 * `uploadPassword` fall back to the run config, then the shared default.
 */
export function resolveUploadTarget(cfg: LangbridgeConfig): UploadTarget {
  if (isDesktop()) {
    return {
      mode: 'proxy',
      base: (cfg.uploadBase || DEFAULT_CONFIG.apiBase).replace(/\/$/, ''),
      // Never fall back to cfg.password here: on desktop that's the LOCAL sidecar's
      // random password, which the CENTRAL storage server rejects (401). Use the
      // injected central credential, else the shared central default.
      password: cfg.uploadPassword || DEFAULT_CONFIG.password,
    };
  }
  return {
    mode: 'presign',
    base: (cfg.uploadBase || cfg.apiBase).replace(/\/$/, ''),
    password: cfg.uploadPassword || cfg.password,
  };
}

/** Client-side validation. Returns a user-facing error message, or null if ok. */
export function validateUpload(mimeType: string | undefined, size: number): string | null {
  const mime = (mimeType || '').toLowerCase();
  if (!UPLOAD_ALLOWED_MIME.includes(mime)) {
    return `지원하지 않는 파일 형식입니다${mimeType ? ` (${mimeType})` : ''}.`;
  }
  if (size > UPLOAD_MAX_BYTES) {
    const mb = Math.round(UPLOAD_MAX_BYTES / (1024 * 1024));
    return `파일이 너무 큽니다. 최대 ${mb}MB까지 업로드할 수 있어요.`;
  }
  return null;
}

function base64ToBlob(base64: string, mimeType: string): Blob {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: mimeType });
}

function pwHeader(password?: string): Record<string, string> {
  return password ? { [CHAT_PASSWORD_HEADER]: password } : {};
}

async function failureMessage(res: Response, label: string): Promise<string> {
  if (res.status === 401) return '업로드 인증에 실패했습니다.';
  const body = await res.text().catch(() => '');
  return `${label} 실패 (${res.status})${body ? ` — ${body.slice(0, 160)}` : ''}`;
}

/** Desktop: multipart POST to the central server (no CORS, no S3 creds client-side). */
async function proxyUpload(
  target: UploadTarget,
  blob: Blob,
  filename: string,
): Promise<string> {
  const fd = new FormData();
  fd.append('file', blob, filename || 'upload');
  const res = await fetch(`${target.base}/storage/upload`, {
    method: 'POST',
    headers: pwHeader(target.password),
    body: fd,
  });
  if (!res.ok) throw new UploadError(await failureMessage(res, '업로드'));
  const data = (await res.json()) as ProxyResponse;
  return data.fileUrl;
}

/** Web: presigned URL then a direct PUT to S3 (requiredHeaders forwarded verbatim). */
async function presignUpload(
  target: UploadTarget,
  blob: Blob,
  filename: string,
  mimeType: string,
): Promise<string> {
  const presignRes = await fetch(`${target.base}/storage/presign`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...pwHeader(target.password) },
    body: JSON.stringify({ contentType: mimeType, size: blob.size, filename }),
  });
  if (!presignRes.ok) throw new UploadError(await failureMessage(presignRes, 'presign'));
  const presign = (await presignRes.json()) as PresignResponse;

  // Forward requiredHeaders exactly — a Content-Type mismatch is rejected by S3 (403).
  const putRes = await fetch(presign.uploadUrl, {
    method: 'PUT',
    headers: presign.requiredHeaders,
    body: blob,
  });
  if (!putRes.ok) throw new UploadError(`파일 업로드에 실패했습니다 (${putRes.status}).`);
  return presign.fileUrl;
}

/**
 * Upload one staged attachment and return a URL-bearing copy (base64 stripped).
 * Attachments that are already remote URLs (workflow url-mode / previously
 * uploaded) pass through untouched. Throws `UploadError` on validation/network
 * failure so callers can surface it and offer retry.
 */
export async function uploadAttachment(
  att: Attachment,
  cfg: LangbridgeConfig,
): Promise<Attachment> {
  if (!att.base64) return att;

  const mimeType = att.mimeType || 'application/octet-stream';
  const blob = base64ToBlob(att.base64, mimeType);
  const err = validateUpload(mimeType, blob.size);
  if (err) throw new UploadError(err);

  const target = resolveUploadTarget(cfg);
  const fileUrl =
    target.mode === 'proxy'
      ? await proxyUpload(target, blob, att.name)
      : await presignUpload(target, blob, att.name, mimeType);

  const { base64: _base64, ...rest } = att;
  return { ...rest, url: fileUrl, mimeType };
}

/** Upload all staged attachments (parallel), preserving order. */
export function uploadAttachments(
  attachments: Attachment[],
  cfg: LangbridgeConfig,
): Promise<Attachment[]> {
  return Promise.all(attachments.map((a) => uploadAttachment(a, cfg)));
}
